import { useState, useCallback } from 'react';
import type { DatasetSummaryDto } from '@/api/types';

export interface DatasetFormData {
  displayName: string;
  instrument: string;
  sourceTimezone: string;
  candlesFile: File | null;
  newsFile: File | null;
}

export interface DatasetFormResult {
  success: boolean;
  errors: string[];
}

interface UseDatasetFormOptions {
  mode: 'create' | 'edit';
  initialDataset?: DatasetSummaryDto | null;
  onSubmit: (data: FormData) => Promise<DatasetFormResult>;
  onSuccess: () => void;
}

export function validateDatasetForm(mode: 'create' | 'edit', formData: DatasetFormData): string | null {
  const { displayName, instrument, sourceTimezone, candlesFile, newsFile } = formData;
  if (mode === 'create' && (!displayName.trim() || !instrument.trim() || !sourceTimezone.trim())) {
    return 'Name, instrument, and candle timezone are required.';
  }
  if (mode === 'create' && (!candlesFile || !newsFile)) {
    return 'Both the 1m candles CSV and the news events CSV are required.';
  }
  if ((candlesFile && !newsFile) || (!candlesFile && newsFile)) {
    return 'Replacing CSV data requires both the 1m candles CSV and the news events CSV.';
  }
  return null;
}

export function useDatasetForm({ mode, initialDataset, onSubmit, onSuccess }: UseDatasetFormOptions) {
  const [formData, setFormData] = useState<DatasetFormData>(() => {
    if (mode === 'edit' && initialDataset) {
      return {
        displayName: initialDataset.displayName ?? '',
        instrument: initialDataset.instrument ?? '',
        sourceTimezone: initialDataset.sourceTimezone ?? '',
        candlesFile: null,
        newsFile: null,
      };
    }
    return {
      displayName: '',
      instrument: initialDataset?.instrument ?? '',
      sourceTimezone: 'UTC',
      candlesFile: null,
      newsFile: null,
    };
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setField = useCallback((field: keyof Omit<DatasetFormData, 'candlesFile' | 'newsFile'>, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const setFile = useCallback((field: 'candlesFile' | 'newsFile', file: File | null) => {
    setFormData((prev) => ({ ...prev, [field]: file }));
  }, []);

  const reset = useCallback(() => {
    setFormData({
      displayName: '',
      instrument: '',
      sourceTimezone: 'UTC',
      candlesFile: null,
      newsFile: null,
    });
    setError(null);
  }, []);

  const submit = useCallback(async (): Promise<DatasetFormResult> => {
    const { displayName, instrument, sourceTimezone, candlesFile, newsFile } = formData;

    const validationError = validateDatasetForm(mode, formData);
    if (validationError) {
      setError(validationError);
      return { success: false, errors: ['Validation failed.'] };
    }

    setBusy(true);
    setError(null);

    try {
      const form = new FormData();
      if (displayName) form.append('displayName', displayName.trim());
      form.append('instrument', instrument.trim());
      form.append('sourceTimezone', sourceTimezone.trim());
      if (candlesFile && newsFile) {
        form.append('candlesCsv', candlesFile);
        form.append('newsCsv', newsFile);
      }

      const result = await onSubmit(form);
      if (!result.success) {
        setError(result.errors.join('\n'));
        return result;
      }

      onSuccess();
      return { success: true, errors: [] };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(msg);
      return { success: false, errors: [msg] };
    } finally {
      setBusy(false);
    }
  }, [formData, mode, onSubmit, onSuccess]);

  return {
    formData,
    error,
    busy,
    setField,
    setFile,
    reset,
    submit,
  };
}
